import '@beyondtrust/btux-iconography-web';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from './breadcrumb-item.styles.scss';

export const BREADCRUMB_ITEM_TAG = 'platform-breadcrumb-item' as const;

/**
 * A single breadcrumb. Put the label in the default slot. Set `href` to render a
 * plain link, set `current` for the current page (plain text + aria-current), or
 * slot your own `<a>` (e.g. a framework router link) for client-side navigation.
 *
 * The parent `<platform-breadcrumbs>` sets the `leading` (first visible crumb)
 * and `in-overflow` (rendered inside the overflow dropdown) attributes to drive
 * the separator and layout — consumers do not set these.
 *
 * @public
 */
@customElement(BREADCRUMB_ITEM_TAG)
export class PlatformBreadcrumbItem extends LitElement {
  static override styles = [styles];

  /** Link target; renders the label as a plain `<a href>`. Ignored when `current`. @public */
  @property() href?: string;

  /** Marks the current page: plain text, no link, `aria-current="page"`. @public */
  @property({ type: Boolean }) current = false;

  /** Each crumb is a list item within the breadcrumbs list. */
  @property({ reflect: true }) override role = 'listitem';

  @state() private _truncated = false;

  // `.content` lives inside the slotted anchor of `<sl-tooltip>`, which lays out a
  // frame after our own `updated()`. A one-shot measurement therefore reads 0x0 and
  // never corrects itself. Observing `.content` instead re-measures whenever it gains
  // (or later changes) real dimensions — including the inline<->overflow transition,
  // where `:host([in-overflow]) .content` drops its max-width cap.
  private _contentResize = new ResizeObserver(() => this._measureTruncation());

  private _observedContent?: Element;

  private get _label(): string {
    return this.textContent?.trim() ?? '';
  }

  override firstUpdated() {
    // Truncation often materializes *after* our first render: the orchestrator
    // lays out the row and squeezes this crumb, and web fonts widen the text —
    // both change `scrollWidth` without resizing `.content`'s (capped) box, so the
    // ResizeObserver never fires. Re-measure after the next frame (post-layout) and
    // once fonts settle to catch these cases.
    requestAnimationFrame(() => this._measureTruncation());
    document.fonts?.ready.then(() => this._measureTruncation());
  }

  override updated() {
    const content = this.renderRoot.querySelector('.content');
    if (content !== this._observedContent) {
      if (this._observedContent) {
        this._contentResize.unobserve(this._observedContent);
      }
      if (content) {
        this._contentResize.observe(content);
      }
      this._observedContent = content ?? undefined;
    }
  }

  override disconnectedCallback() {
    this._contentResize.disconnect();
    this._observedContent = undefined;
    super.disconnectedCallback();
  }

  private _measureTruncation() {
    const content = this.renderRoot.querySelector<HTMLElement>('.content');
    this._truncated = !!content && content.scrollWidth > content.clientWidth;
  }

  override render() {
    const label = html`<span class="content" part="content"><slot></slot></span>`;
    const crumb = this.current
      ? html`<span class="crumb current" part="link" aria-current="page"
          ><span class="content" part="content">${this._label}</span></span
        >`
      : this.href != undefined
        ? html`<a class="crumb" part="link" href=${this.href}>${label}</a>`
        : html`<span class="crumb" part="link">${label}</span>`;

    return html`
      <btux-svg class="separator" part="separator" name="chevron-right" aria-hidden="true"></btux-svg>
      <sl-tooltip class="tip" hoist content=${this._label} ?disabled=${!this._truncated}>${crumb}</sl-tooltip>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [BREADCRUMB_ITEM_TAG]: PlatformBreadcrumbItem;
  }
}
